import { forwardRef } from 'react';

import { useController } from 'react-hook-form';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { callAllFn, mergeRefs } from '@julofinance/web-helpers/dist/fn';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import type { InputProps } from '@new-components/elements/input';
import { FieldGroup } from '@new-components/elements';
import { useFormContext } from '@hooks/react-hook-form';

const maxDate = dayjs().subtract(18, 'year');

const theme = createTheme({
  components: {
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '0px !important',
        },
      },
    },
  },
});

const DatePicker = forwardRef<HTMLDivElement, InputProps>((props, ref) => {
  const {
    required,
    rules,
    helperText,
    className,
    name,
    isInvalid,
    onBlur,
    onChange,
    ...resProps
  } = props;

  const { control } = useFormContext();
  const {
    fieldState: { error, invalid },
    field: {
      ref: controlRef,
      onChange: controllerOnChange,
      onBlur: controllerOnBlur,
      value: controllerValue,
    },
  } = useController({
    name,
    control,
    rules: {
      ...(required && { required: 'This field is required' }),
      ...(rules && rules),
    },
  });

  const handleOnChange = (date: Dayjs) => {
    controllerOnChange({ target: { value: date.format('YYYY-MM-DD') } });
  };

  return (
    <FieldGroup
      ref={ref}
      invalid={isInvalid || invalid}
      error={error}
      helperText={helperText}
      className={className}
      name={name}
    >
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            value={controllerValue ? dayjs(controllerValue) : null}
            label={props.label}
            disabled={props.disabled}
            maxDate={maxDate}
            onChange={callAllFn(onChange, handleOnChange)}
            slotProps={{
              textField: {
                inputRef: mergeRefs(controlRef, ref),
                helperText: helperText,
                variant: 'standard',
                onBlur: callAllFn(onBlur, controllerOnBlur),
                ...resProps,
              },
            }}
            format='DD/MM/YYYY'
          />
        </LocalizationProvider>
      </ThemeProvider>
    </FieldGroup>
  );
});

export default DatePicker;
